import {
  harvestLossMachineLabel,
} from "./harvestLossMetrics";

function sanitizeFilePart(value) {
  return String(value || "perda-colheita")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9._-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);
}

function escapeCsvCell(value) {
  const str = value == null ? "" : String(value);
  if (/[",;\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function formatExportNumber(value, digits = 2) {
  if (value == null || !Number.isFinite(Number(value))) return "";
  return Number(value).toLocaleString("pt-BR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function formatExportInteger(value) {
  if (value == null || !Number.isFinite(Number(value))) return "";
  return Math.round(Number(value)).toLocaleString("pt-BR");
}

function formatExportTimestamp(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("pt-BR");
}

function rollingAverageLabel(summary, t) {
  const window = summary && summary.rollingWindow;
  if (!window || window <= 1) return null;
  return t("Média últimas {{count}}", { count: window });
}

function pushSummaryRows(rows, machineLabel, summary, t) {
  if (!summary || !summary.sampleCount) return;

  const { lastSample, avgLast3, avgTotal } = summary;
  const rollingLabel = rollingAverageLabel(summary, t);

  if (lastSample) {
    rows.push([
      machineLabel,
      t("Última amostra"),
      formatExportInteger(lastSample.seedCount),
      formatExportNumber(lastSample.kgPerHa),
      formatExportNumber(lastSample.bagsPerHa, 3),
    ]);
  }

  if (rollingLabel) {
    rows.push([
      machineLabel,
      rollingLabel,
      formatExportInteger(avgLast3.seedCount),
      formatExportNumber(avgLast3.kgPerHa),
      formatExportNumber(avgLast3.bagsPerHa, 3),
    ]);
  }

  rows.push([
    machineLabel,
    t("Média total do período"),
    formatExportInteger(avgTotal.seedCount),
    formatExportNumber(avgTotal.kgPerHa),
    formatExportNumber(avgTotal.bagsPerHa, 3),
  ]);
}

export function buildHarvestLossSpreadsheetFileName({ observationId, orderLabel }) {
  const order = sanitizeFilePart(orderLabel || observationId || "ordem");
  const date = new Date().toISOString().slice(0, 10);
  return `perda_colheita_${order}_${date}.csv`;
}

export function buildHarvestLossSpreadsheetCsvRows({
  machineBreakdown,
  samples,
  harvestConfig,
  orderLabel,
  t,
}) {
  const rows = [];
  const title = orderLabel
    ? t("Planilha de perda de colheita — {{order}}", { order: orderLabel })
    : t("Planilha de perda de colheita");

  rows.push([title]);
  rows.push([]);

  if (harvestConfig) {
    rows.push([
      t("PMS (g)"),
      harvestConfig.pmsG != null ? formatExportNumber(harvestConfig.pmsG, 0) : "",
      t("Margem tolerada (kg/ha)"),
      harvestConfig.toleratedLossKgHa != null
        ? formatExportNumber(harvestConfig.toleratedLossKgHa)
        : "",
    ]);
    rows.push([]);
  }

  rows.push([
    t("Máquina"),
    t("Métrica"),
    t("Sementes"),
    t("kg/ha"),
    t("sc/ha"),
  ]);

  if (machineBreakdown && machineBreakdown.machines && machineBreakdown.machines.length) {
    machineBreakdown.machines.forEach((machine) => {
      pushSummaryRows(
        rows,
        harvestLossMachineLabel(machine.machineKey, t),
        machine.summary,
        t
      );
    });

    if (
      machineBreakdown.machines.length > 1 &&
      machineBreakdown.overall &&
      machineBreakdown.overall.sampleCount
    ) {
      pushSummaryRows(
        rows,
        t("Total geral (todas as máquinas)"),
        machineBreakdown.overall,
        t
      );
    }
  }

  rows.push([]);
  rows.push([t("Amostras registradas")]);
  rows.push([
    t("Máquina"),
    t("Repetição"),
    t("Data/Hora"),
    t("Operador"),
    t("Sementes"),
    t("kg/ha"),
    t("sc/ha"),
    t("Latitude"),
    t("Longitude"),
    t("Observações"),
  ]);

  (samples || []).forEach((sample) => {
    rows.push([
      harvestLossMachineLabel(sample.machineName || "", t),
      sample.repetitionNumber != null ? String(sample.repetitionNumber) : "",
      formatExportTimestamp(sample.timestamp),
      sample.operatorName || "",
      formatExportInteger(sample.seedCount),
      formatExportNumber(sample.kgPerHa),
      formatExportNumber(sample.bagsPerHa, 3),
      sample.latitude != null ? Number(sample.latitude).toFixed(6) : "",
      sample.longitude != null ? Number(sample.longitude).toFixed(6) : "",
      sample.notes || "",
    ]);
  });

  return rows;
}

export function downloadHarvestLossSpreadsheetCsv(rows, fileName) {
  if (!rows || !rows.length) return;

  const content =
    `\uFEFF${rows.map((row) => row.map(escapeCsvCell).join(";")).join("\n")}`;
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName || "perda_colheita.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function buildHarvestLossCollapsedPreviewLines(machineBreakdown, t) {
  if (!machineBreakdown || !machineBreakdown.machines || !machineBreakdown.machines.length) {
    return [];
  }

  const lines = machineBreakdown.machines.map((machine) => ({
    key: machine.machineKey,
    label: harvestLossMachineLabel(machine.machineKey, t),
    sampleCount: machine.summary && machine.summary.sampleCount,
    avgKgPerHa:
      machine.summary &&
      machine.summary.avgTotal &&
      machine.summary.avgTotal.kgPerHa,
    avgBagsPerHa:
      machine.summary &&
      machine.summary.avgTotal &&
      machine.summary.avgTotal.bagsPerHa,
    isTotal: false,
  }));

  if (
    machineBreakdown.machines.length > 1 &&
    machineBreakdown.overall &&
    machineBreakdown.overall.sampleCount
  ) {
    lines.push({
      key: "__total__",
      label: t("Total geral"),
      sampleCount: machineBreakdown.overall.sampleCount,
      avgKgPerHa:
        machineBreakdown.overall.avgTotal &&
        machineBreakdown.overall.avgTotal.kgPerHa,
      avgBagsPerHa:
        machineBreakdown.overall.avgTotal &&
        machineBreakdown.overall.avgTotal.bagsPerHa,
      isTotal: true,
    });
  }

  return lines;
}
